/**
 * Relay push governor — owns the single SSE connection to GET /api/stream
 * and fans frames out to topic subscribers (accountStore, useHeadroom,
 * useRateLimitStatus, useUserKalshiPOS). The polling loops those consumers
 * already have stay intact as the FALLBACK: each consumer asks
 * `topicActive(topic)` before arming its timer and re-arms it whenever push
 * drops (`onActiveChange`). Mixed-version safe: an old relay 404s /api/stream
 * and everything polls exactly as today.
 *
 * Transport: SSE over fetch-streaming (Authorization header works; Apache
 * mod_rewrite [P] on the prod lanes carries it as a plain chunked response —
 * WebSocket upgrades would not traverse it). Recovery is reconnect +
 * fresh snapshots from the server; no replay. Stall detector: no bytes
 * (heartbeats count) for 40s → reconnect.
 *
 * Topic availability: the server sends {__unavailable: true} for topics it
 * can't stream for this session (e.g. orders/pos for personal-key scope) —
 * those consumers keep polling while the rest ride the stream.
 *
 * Low bandwidth mode (`src/stores/lowBandwidth.ts`): the request carries
 * `lowBandwidth=1` and a flip reconnects. An org-scope relay then drops
 * `orders` and `positions` and announces `hello.derived` — both ride `pos`
 * frames, and this module rebuilds them in their own topics' shapes for their
 * subscribers (deriveFromPos). Never just omit a topic: `topicActive` is true
 * for a topic that is never sent, so its consumer would freeze with no frames
 * and no REST fallback. A relay without `derived` behaves exactly as before.
 */
import { apiFetch, useAuthStore } from '../stores/authStore';
import { lowBandwidthEnabled, subscribeLowBandwidth } from '../stores/lowBandwidth';
import { createSseParser } from '../lib/streamParser';
import { probePushApply } from '../lib/trading/feedLatencyProbe';
import { createRelayClock, elapsedSince, localClockStamp, type LocalClockStamp, type RelayClock } from '../lib/relayClock';

export type PushTopic =
  | 'positions'
  | 'pnl'
  | 'orders'
  | 'pos'
  | 'fills'
  | 'balance'
  | 'headroom'
  | 'rateLimit'
  // RFQ lane (relay/src/push/push.ts must list the same names). Lane-org +
  // rfq-grant scoped server-side; a session outside the lane gets
  // {__unavailable:true} and the RFQ stores keep polling.
  | 'rfq.health'
  | 'rfq.board'
  | 'rfq.decisions'
  | 'rfq.quotes'
  | 'rfq.firehose'
  | 'rfq.proposals';

const ALL_TOPICS: PushTopic[] = [
  'positions',
  'pnl',
  'orders',
  'pos',
  'fills',
  'balance',
  'headroom',
  'rateLimit',
  'rfq.health',
  'rfq.board',
  'rfq.decisions',
  'rfq.quotes',
  'rfq.firehose',
  'rfq.proposals',
];

function authScopeKey(): string {
  const user = useAuthStore.getState().user;
  return JSON.stringify([user?.id, user?.orgId, user?.role]);
}

const STALL_MS = 40_000;
// The org poller republishes orders/pos at least every 10s while healthy,
// including unchanged books. Three missed publications restore read polling;
// transport heartbeats and unrelated topics cannot extend this deadline.
const SNAPSHOT_STALL_MS = 30_000;
const SNAPSHOT_TOPICS: readonly PushTopic[] = ['orders', 'pos'];
const UNAVAILABLE_RETRY_MS = 5 * 60_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
/** Derived topics (hello.derived): the topics this module can rebuild from a
 *  `pos` frame. `pos` = {positions, orders + queue fields, queueStatus,
 *  ordersAsOf}; `orders` = {orders, ordersAsOf}; `positions` = {positions}. */
const POS_DERIVABLE: readonly PushTopic[] = ['orders', 'positions'];
/** Per-order fields the relay merges into `pos` (push.ts resolveTopic). */
const POS_QUEUE_FIELDS = ['queue_position', 'queue_position_fp', 'queue_position_observed_at'] as const;
/** A derived frame whose content is unchanged is not re-handed to subscribers
 *  more often than this (the relay's own orders/positions frames are
 *  hash-suppressed the same way and re-sent every 10 s). It still renews the
 *  topic's freshness clock at once. Well under the 30 s orders stale mark. */
const DERIVED_REDELIVER_MS = 5_000;

const pushEnabled =
  import.meta.env.VITE_RELAY_PUSH === '1' || import.meta.env.VITE_RELAY_PUSH === 'true';

export interface PushFrame {
  topic: PushTopic;
  /** Relay emit stamp (epoch ms) — the `__pushLatency()` probe's wire mark. */
  ts: number;
  payload: unknown;
}

type TopicCb = (payload: unknown, clock?: RelayClock | null) => void;

const topicSubs = new Map<PushTopic, Set<TopicCb>>();
const activeChangeCbs = new Set<() => void>();
const unavailableTopics = new Set<PushTopic>();
const staleTopics = new Set<PushTopic>();
const snapshotReceivedAt = new Map<PushTopic, LocalClockStamp>();
/** This connection's topics that ride `pos` frames (hello.derived). */
const derivedFromPos = new Set<PushTopic>();
/** Per derived topic: the content key last handed to subscribers, and when. */
const derivedDelivered = new Map<PushTopic, { key: string; at: LocalClockStamp }>();

let streamActive = false;
/** The low-bandwidth flag the current stream request was opened with. */
let requestedLowBandwidth = false;
let running = false;
let runGeneration = 0;
let abort: AbortController | null = null;
let backoffMs = BACKOFF_BASE_MS;
let unavailableSince: LocalClockStamp | null = null;
let stallTimer: ReturnType<typeof setInterval> | null = null;

function setStreamActive(next: boolean): void {
  if (streamActive === next) return;
  streamActive = next;
  staleTopics.clear();
  snapshotReceivedAt.clear();
  derivedDelivered.clear();
  if (!next) {
    unavailableTopics.clear();
    derivedFromPos.clear();
  } else for (const topic of SNAPSHOT_TOPICS) snapshotReceivedAt.set(topic, localClockStamp());
  notifyActiveChange();
}

function notifyActiveChange(): void {
  for (const cb of activeChangeCbs) {
    try {
      cb();
    } catch {
      // subscriber's problem
    }
  }
}

/** True when the stream is up AND the server can serve this topic. Consumers
 *  arm their polling timers when this is false. */
export function topicActive(topic: PushTopic): boolean {
  return streamActive && !unavailableTopics.has(topic) && !staleTopics.has(topic);
}

export function isPushActive(): boolean {
  return streamActive;
}

/** Fires on any stream up/down or topic-availability change — consumers
 *  re-evaluate their polling from it. Returns unsubscribe. */
export function onActiveChange(cb: () => void): () => void {
  activeChangeCbs.add(cb);
  return () => activeChangeCbs.delete(cb);
}

/** Credentials/key mode changed without necessarily rotating the auth token.
 * Abort the old scope immediately; the existing loop reconnects and polling
 * covers the gap. This never opens a second concurrent connection. */
export function invalidateRelayPushSession(): void {
  abort?.abort();
  setStreamActive(false);
}

/** Subscribe to a topic's frames. First subscriber overall starts the
 *  connection; last one stops it. Returns unsubscribe. */
export function subscribePush(topic: PushTopic, cb: TopicCb): () => void {
  let set = topicSubs.get(topic);
  if (!set) topicSubs.set(topic, (set = new Set()));
  set.add(cb);
  ensureRunning();
  return () => {
    set.delete(cb);
    if (set.size === 0) topicSubs.delete(topic);
    if (topicSubs.size === 0) stop();
  };
}

/** Fan one decoded `update` frame out to its topic subscribers. Exported for
 *  the push-latency probe test; the stream loop is the only runtime caller. */
export function dispatchPushFrame(frame: PushFrame, clock: RelayClock | null = null): void {
  // relay `ts` → apply: the envelope stamp used to be dropped here, so the
  // relay→browser leg was unmeasurable. Stamp before the availability
  // bookkeeping so a topic's first frame counts too. A derived topic is not
  // stamped: its sample is this same `pos` frame's.
  if (typeof frame.ts === 'number') probePushApply(frame.topic, frame.ts);
  let changed = applyFrame(frame.topic, frame.payload, clock, true);
  if (frame.topic === 'pos') {
    for (const topic of derivedFromPos) {
      const derived = deriveFromPos(topic, frame.payload);
      changed = applyFrame(topic, derived.payload, clock, redeliver(topic, derived.key)) || changed;
    }
  }
  if (changed) notifyActiveChange();
}

/** Availability bookkeeping + subscriber fan-out for one topic's payload.
 *  `deliver: false` renews the topic's delivery evidence without handing an
 *  unchanged derived payload to its subscribers again. Returns whether the
 *  topic's availability changed (the caller notifies once). */
function applyFrame(topic: PushTopic, payload: unknown, clock: RelayClock | null, deliver: boolean): boolean {
  const p = payload as { __unavailable?: boolean } | null;
  if (p && typeof p === 'object' && p.__unavailable === true) {
    derivedDelivered.delete(topic);
    if (unavailableTopics.has(topic)) return false;
    unavailableTopics.add(topic);
    return true;
  }
  // Delivery evidence is separate from physical queue freshness. Never stamp
  // queueStatus or order observations here. Consumers still validate them.
  const snapshotTopic = SNAPSHOT_TOPICS.includes(topic);
  const envelope = p as { orders?: unknown; positions?: unknown } | null;
  const snapshotEnvelope = envelope && Array.isArray(envelope.orders) &&
    (topic !== 'pos' || Array.isArray(envelope.positions));
  const delivered = !snapshotTopic || !!snapshotEnvelope;
  if (snapshotTopic && delivered) snapshotReceivedAt.set(topic, localClockStamp());
  const wasUnavailable = delivered && unavailableTopics.delete(topic);
  const wasStale = delivered && staleTopics.delete(topic);
  if (deliver) {
    const subs = topicSubs.get(topic);
    for (const cb of subs ?? []) {
      try {
        cb(payload, clock);
      } catch (err) {
        console.error(`[relay-push] ${topic} subscriber threw:`, err);
      }
    }
  }
  return wasUnavailable || wasStale;
}

/** A `pos` payload in a derived topic's own shape, plus its content key (null
 *  = not comparable: always delivered). `orders` drops the per-order queue
 *  fields and keeps `ordersAsOf` out of the key, as the relay's orders hash
 *  does (#968); `positions` is the same fold the positions topic resolves. An
 *  unavailable `pos` passes through, so both fall back to their REST polls. */
function deriveFromPos(topic: PushTopic, payload: unknown): { payload: unknown; key: string | null } {
  if (!payload || typeof payload !== 'object') return { payload, key: null };
  const p = payload as Record<string, unknown>;
  if (p.__unavailable === true) return { payload, key: null };
  if (topic === 'positions') {
    return { payload: { positions: p.positions }, key: Array.isArray(p.positions) ? JSON.stringify(p.positions) : null };
  }
  if (!Array.isArray(p.orders)) return { payload: { orders: p.orders, ordersAsOf: p.ordersAsOf ?? null }, key: null };
  const orders = p.orders.map(withoutQueueFields);
  // The dormant depth supplement rides with the orders it describes.
  const content = 'depth' in p ? { orders, depth: p.depth } : { orders };
  return { payload: { ...content, ordersAsOf: p.ordersAsOf ?? null }, key: JSON.stringify(content) };
}

function withoutQueueFields(order: unknown): unknown {
  if (!order || typeof order !== 'object' || Array.isArray(order)) return order;
  const o = order as Record<string, unknown>;
  if (!POS_QUEUE_FIELDS.some((f) => f in o)) return order;
  const out: Record<string, unknown> = { ...o };
  for (const f of POS_QUEUE_FIELDS) delete out[f];
  return out;
}

/** Whether a derived payload goes to subscribers: a change, an uncomparable
 *  payload, or the same content again after DERIVED_REDELIVER_MS. */
function redeliver(topic: PushTopic, key: string | null): boolean {
  if (key === null) {
    derivedDelivered.delete(topic);
    return true;
  }
  const last = derivedDelivered.get(topic);
  if (last && last.key === key && elapsedSince(last.at) < DERIVED_REDELIVER_MS) return false;
  derivedDelivered.set(topic, { key, at: localClockStamp() });
  return true;
}

/** Read `hello.derived`. A topic the relay derives from something this module
 *  cannot rebuild is marked unavailable, so its consumer polls instead of
 *  waiting for frames that will never come. Returns whether that changed. */
function applyDerivedTopics(raw: unknown): boolean {
  derivedFromPos.clear();
  derivedDelivered.clear();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  let changed = false;
  for (const [topic, source] of Object.entries(raw as Record<string, unknown>)) {
    if (!(ALL_TOPICS as readonly string[]).includes(topic)) continue;
    const t = topic as PushTopic;
    if (source === 'pos' && POS_DERIVABLE.includes(t)) derivedFromPos.add(t);
    else if (!unavailableTopics.has(t)) {
      unavailableTopics.add(t);
      changed = true;
    }
  }
  return changed;
}

function ensureRunning(): void {
  if (!pushEnabled || running) return;
  running = true;
  void connectLoop(++runGeneration);
}

function stop(): void {
  running = false;
  ++runGeneration;
  abort?.abort();
  abort = null;
  if (stallTimer) clearInterval(stallTimer);
  stallTimer = null;
  setStreamActive(false);
}

async function connectLoop(generation: number): Promise<void> {
  const isCurrentRun = () => running && generation === runGeneration;
  while (isCurrentRun()) {
    // Not logged in yet / logged out → wait, don't spin.
    if (!useAuthStore.getState().token) {
      await sleep(2000);
      continue;
    }
    if (unavailableSince && elapsedSince(unavailableSince) < UNAVAILABLE_RETRY_MS) {
      await sleep(5000);
      continue;
    }
    try {
      const controller = new AbortController();
      abort = controller;
      // Polling recovery must not wait for a browser body reader to settle.
      // An old run's delayed abort must not disable its replacement.
      controller.signal.addEventListener('abort', () => {
        if (isCurrentRun() && abort === controller) setStreamActive(false);
      }, { once: true });
      const requestStarted = localClockStamp();
      const sessionToken = useAuthStore.getState().token;
      const sessionScope = _lastScope;
      let relayClock: RelayClock | null = null;
      requestedLowBandwidth = lowBandwidthEnabled();
      const res = await apiFetch(
        `/api/stream?topics=${ALL_TOPICS.join(',')}${requestedLowBandwidth ? '&lowBandwidth=1' : ''}`,
        { signal: controller.signal },
      );
      if (!isCurrentRun() || controller.signal.aborted) {
        // Cleanup cannot own reconnect liveness; cancellation can reject or
        // remain pending independently of this subscription run.
        void res.body?.cancel().catch(() => {});
        if (!isCurrentRun()) return;
        // Token rotation aborts the request, not the subscription run. An
        // already-fulfilled/bodyless response may still reach this await;
        // do not leave running=true with no loop left to reconnect.
        throw new DOMException('Stream request aborted', 'AbortError');
      }
      if (res.status === 404) {
        // Push disabled on this lane (or old relay) — back off for a while.
        unavailableSince = localClockStamp();
        setStreamActive(false);
        continue;
      }
      if (!res.ok || !res.body) {
        throw new Error(`stream HTTP ${res.status}`);
      }

      unavailableSince = null;
      // Local duration, not an epoch deadline: a backward clock correction
      // must not strand a dead stream. The wall-time bound also detects sleep
      // on browsers whose monotonic clock pauses. A forward jump may cause a
      // conservative read-only reconnect; it never replays trading actions.
      let lastByteAt = localClockStamp();
      stallTimer = setInterval(() => {
        if (!isCurrentRun() || controller.signal.aborted) return;
        let changed = false;
        if (streamActive) for (const topic of SNAPSHOT_TOPICS) {
          const received = snapshotReceivedAt.get(topic);
          if (received && elapsedSince(received) >= SNAPSHOT_STALL_MS && !staleTopics.has(topic)) {
            staleTopics.add(topic);
            changed = true;
          }
        }
        if (changed) notifyActiveChange();
        if (elapsedSince(lastByteAt) > STALL_MS) {
          console.warn('[relay-push] stall — reconnecting');
          controller.abort();
        }
      }, 5000);

      const parser = createSseParser(
        (e) => {
          // Buffered old-user bytes can arrive after abort; never dispatch
          // them into the next account's store or sound session.
          if (!isCurrentRun() || controller.signal.aborted || sessionToken !== useAuthStore.getState().token
            || sessionScope !== _lastScope) return;
          if (e.event === 'bye') {
            // Deploy goodbye — reconnect promptly with jitter so the herd
            // spreads across the new process's first second.
            setTimeout(() => controller.abort(), Math.random() * 3000);
            return;
          }
          if (e.event === 'hello') {
            let hello: { serverTs?: unknown; derived?: unknown } | null = null;
            try { hello = JSON.parse(e.data); } catch { hello = null; }
            try { relayClock = createRelayClock(hello?.serverTs, requestStarted); }
            catch { relayClock = null; }
            // Before going active, so no consumer sees a derived topic as live
            // without its source. Old relay: no `derived`, nothing changes.
            const derivedChanged = applyDerivedTopics(hello?.derived);
            backoffMs = BACKOFF_BASE_MS;
            const wasActive = streamActive;
            setStreamActive(true);
            if (wasActive && derivedChanged) notifyActiveChange();
            return;
          }
          if (e.event === 'update') {
            try {
              dispatchPushFrame(JSON.parse(e.data) as PushFrame, relayClock);
            } catch {
              // malformed frame — drop; next snapshot heals
            }
          }
        },
        () => {
          // heartbeat comment — liveness only
        },
      );

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        lastByteAt = localClockStamp();
        parser(decoder.decode(value, { stream: true }));
      }
    } catch {
      // abort / network — fall through to reconnect
    } finally {
      // stop() already clears this run's timer. A replacement can start
      // before the old aborted reader settles; old cleanup must not own it.
      if (generation === runGeneration) {
        if (stallTimer) clearInterval(stallTimer);
        stallTimer = null;
        setStreamActive(false);
      }
    }
    if (!isCurrentRun()) break;
    const delay = Math.random() * backoffMs;
    backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
    await sleep(delay);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// Token change (login/logout/refresh-rotation) → tear the stream down NOW.
// Without this a logged-out tab's stream would keep delivering the previous
// user's frames and repopulate caches the logout wipe just cleared (the same
// class of leak the hooks' epoch guards exist for).
let _lastScope = authScopeKey();
let _lastToken: string | null = useAuthStore.getState().token ?? null;
useAuthStore.subscribe((s) => {
  const token = s.token ?? null;
  const scope = authScopeKey();
  if (token !== _lastToken || scope !== _lastScope) {
    _lastToken = token;
    _lastScope = scope;
    abort?.abort();
  }
});

// Low bandwidth mode flipped (Settings, the StatusBar chip, a manager's change
// arriving through /me) → reconnect so the relay applies its floors and topic
// derivation. Same path as a token rotation: polling covers the gap. The
// notifier also fires for "show for this game" holds, which do not change the
// flag, and a flip between attempts is simply read by the next one.
subscribeLowBandwidth(() => {
  if (lowBandwidthEnabled() !== requestedLowBandwidth) abort?.abort();
});
